import { supportedPasteMimeTypes } from '../helpers/paste-helper';

/**
 * Takes pasted content and reads the content into a string, without any parsing. If there is a problem with the picked content
 * (for example is has an unsupported MIME type), an error is thrown.
 */
export async function readPasteContent(content: DataTransferItemList): Promise<
  | {
      success: true;
      items: {
        type: string;
        rawContent: string;
      }[];
    }
  | {
      success: false;
      error:
        | { tag: 'UNSUPPORTED_MIME_TYPE'; fileType: string }
        | { tag: 'UNEXPECTED_DATA_TYPE' };
    }
> {
  const items = Array.from(content);
  const stringItems = items.filter((item) => item.kind === 'string');
  if (stringItems.length === 0) {
    return { success: false, error: { tag: 'UNEXPECTED_DATA_TYPE' } };
  }

  const mimeTypes = Object.keys(supportedPasteMimeTypes);
  const supportedItems = stringItems.filter((item) => mimeTypes.includes(item.type));
  if (supportedItems.length === 0) {
    return {
      success: false,
      error: {
        tag: 'UNSUPPORTED_MIME_TYPE',
        fileType: stringItems.map((item) => item.type).join(', '),
      },
    };
  }

  // Deconstruct each possible item for type and rawContent (we use allSettled to get all content despite a rejection)
  const itemsResults = await Promise.allSettled(
    supportedItems.map(async (item) => {
      const type = item.type;
      const rawContent = await new Promise<string>((resolve, reject) => {
        item.getAsString((data) => {
          if (data !== null) {
            resolve(data);
          } else {
            reject('Failed to read content');
          }
        });
      });
      return {
        type,
        rawContent,
      };
    })
  );

  const itemsContent = itemsResults
    .filter((result) => result.status === 'fulfilled')
    .map((result) => result.value);

  return {
    success: true,
    items: itemsContent,
  };
}
